<template>
    <div class="tasks-page container mt-5">
      <h1 class="Title">Tâches</h1>
  
      <!-- Bouton pour afficher toutes les tâches -->
      <div class="mb-3">
        <b-button 
          :variant="showAllTasks ? 'primary' : 'outline-primary'" 
          size="sm"
          @click="toggleShowAllTasks"
        >
          {{ showAllTasks ? 'Afficher mes tâches' : 'Afficher toutes les tâches' }}
        </b-button>
      </div>
  
      <!-- Filtres + Recherche -->
      <div class="filters mb-3">
        <!-- Filtre client (pleine largeur) -->
        <b-form-group label="Rechercher un client :" class="mb-3 client-filter-container">
          <b-form-input
            v-model="clientFilterSearch"
            placeholder="Tapez pour rechercher un client..."
            @input="filterClientsForFilter"
            @focus="onClientFilterFocus"
            @blur="onClientFilterBlur"
          />
          <div v-if="showClientFilterResults && filteredClientFilterOptions.length > 0" class="client-search-results mt-2">
            <div
              v-for="client in filteredClientFilterOptions"
              :key="client.value"
              class="client-option p-2 border-bottom cursor-pointer"
              @click="selectClientForFilter(client)"
              :class="{ 'bg-light': selectedClient === client.value }"
            >
              {{ client.text }}
            </div>
          </div>
          <div v-else-if="showClientFilterResults && clientFilterSearch && filteredClientFilterOptions.length === 0" class="text-muted mt-2">
            Aucun client trouvé
          </div>
        </b-form-group>
  
        <!-- Statut et Recherche côte à côte -->
        <div class="d-flex gap-3">
          <b-form-group label="Statut" class="mb-0 flex-grow-1">
          <b-form-select v-model="selectedStatut" :options="statutOptions">
            <template #first>
              <option :value="null">Tous</option>
            </template>
          </b-form-select>
        </b-form-group>
  
          <b-form-group label="Recherche" class="mb-0 flex-grow-1">
          <b-form-input v-model="search" placeholder="Recherche par titre..." />
        </b-form-group>
        </div>
      </div>
  
      <!-- Liste des tâches -->
      <ul class="list-group">
        <li v-for="task in filteredTasks" :key="task.id" class="list-group-item d-flex justify-content-between align-items-center">
          <div class="text-start">
            <span class="text-muted me-2">#{{ task.id }}</span>
            <b-badge :variant="getStatutLabel(task.statut).color" class="me-2 align-middle">
              {{ getStatutLabel(task.statut).label }}
            </b-badge>
            <strong>{{ task.title }}</strong><br>
            Client : {{ task.client_name || '— Aucun —' }}<br>
            Utilisateur : {{ task.user_name || '—' }}
          </div>
          <b-button size="sm" variant="outline-secondary" @click="openDetailModal(task)">Détails</b-button>
        </li>
      </ul>
  
      <!-- Modale de création -->
      <b-modal v-model="showNewTaskModal" title="Créer une nouvelle tâche" hide-footer>
        <b-form @submit.prevent="createTask">
          <b-form-group label="Client :">
            <b-form-select v-model="newTaskClient" :options="clientOptions">
              <template #first>
                <option :value="null">(Aucun client)</option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group label="Titre de la tâche :">
            <b-form-input v-model="newTaskTitle" placeholder="Titre de la tâche…" required />
          </b-form-group>
          <div class="text-end">
            <b-button variant="secondary" @click="showNewTaskModal = false">Annuler</b-button>
            <b-button variant="success" type="submit">Créer</b-button>
          </div>
        </b-form>
      </b-modal>
  
      <!-- Modale détail -->
      <b-modal
        v-model="isDetailModalVisible"
        :title="detailTask ? `Détail de la tâche #${detailTask.id}` : 'Détail de la tâche'"
        hide-footer
        body-class="modal-detail-content"
      >
        <div v-if="detailTask">

          <div class="intervention-info-group">
            <div class="info-row">
              <span class="info-label">Titre</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailTask.title }}</strong></span>
            </div>
            <div v-if="detailTask.client_name" class="info-row">
              <span class="info-label">Client</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailTask.client_name }}</strong></span>
            </div>
            <div v-if="detailTask.user_name" class="info-row">
              <span class="info-label">Utilisateur</span>
              <span class="info-separator">:</span>
              <span class="info-value"><strong>{{ detailTask.user_name }}</strong></span>
            </div>
            <div class="info-row">
              <span class="info-label">Statut</span>
              <span class="info-separator">:</span>
              <span class="info-value">
            <b-badge :variant="getStatutLabel(detailTask.statut).color">
              {{ getStatutLabel(detailTask.statut).label }}
            </b-badge>
              </span>
            </div>
          </div>

          <div v-if="detailTask.memo" class="memo-container mb-2">
            <div class="memo-title-overlay">Mémo</div>
            <div class="memo-display-truncated" @click="openMemoModal(detailTask.memo)">
              <div v-html="cleanMemoHtml(detailTask.memo)" class="memo-content-truncated"></div>
            </div>
          </div>

          <p v-if="photoModalPhotos && photoModalPhotos.length">
            <strong>Photos :</strong>
                <b-button
                size="sm"
                variant="link"
                class="p-0 ms-2 align-baseline"
                @click="photoModalVisible = true"
                >
                Voir ({{ photoModalPhotos.length }})
                </b-button>
          </p>

          <p v-if="linkedInterventions && linkedInterventions.length">
            <strong>Interventions liées :</strong>
            <b-button
              size="sm"
              variant="link"
              class="p-0 ms-2 align-baseline"
              @click="showLinkedInterventionsModal = true"
            >
              Voir ({{ linkedInterventions.length }})
            </b-button>
          </p>

          <p v-if="linkedDocuments && linkedDocuments.length">
            <strong>Documents liés :</strong>
            <b-button
              size="sm"
              variant="link"
              class="p-0 ms-2 align-baseline"
              @click="showLinkedDocumentsModal = true"
            >
              Voir ({{ linkedDocuments.length }})
            </b-button>
          </p>
  
                     <div class="mt-3">
             <!-- Première ligne : boutons principaux -->
             <div class="d-flex justify-content-between mb-2">
               <div class="d-flex gap-2">
            <b-button size="sm" variant="primary" @click="goToEdit(detailTask.id)">
                Modifier
            </b-button>
                 <b-button size="sm" variant="success" @click="createInterventionFromTask">
                     Créer une intervention
                 </b-button>
               </div>
             </div>

            <!-- Deuxième ligne : menus déroulants -->
            <div class="d-flex gap-2 flex-wrap">
            <div class="dropdown">
            <button
                class="btn btn-danger btn-sm dropdown-toggle"
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
            >
                Changer le statut
            </button>
            <ul class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                <li v-for="opt in statutOptions" :key="opt.value">
                <a class="dropdown-item" href="#" @click.prevent="changeTaskStatus(detailTask.id, opt.value)">
                    {{ opt.text }}
                </a>
                </li>
            </ul>
               </div>

                <b-button
                    size="sm"
                    variant="info"
                    @click="openLinkInterventionModal"
                    class="me-2"
                >
                    Lier une intervention existante
                </b-button>
                <b-button
                    size="sm"
                    variant="success"
                    @click="openLinkDocumentModal"
                >
                    Lier un document
                </b-button>
            </div>
            </div>
        </div>
      </b-modal>
  
      <!-- Footer -->
      <FooterActions
        :buttons="[
          { text: 'Nouvelle tâche', onClick: () => { showNewTaskModal = true } },
          { text: 'Accueil', onClick: () => $router.push('/accueil'), variant: 'secondary' }
        ]"
      />
    </div>
    <b-modal v-model="photoModalVisible" title="Photos associées" hide-footer>
        <div class="d-flex flex-wrap">
            <div
            v-for="photo in photoModalPhotos"
            :key="photo.id"
            class="me-2 mb-2"
            style="width: 80px; cursor: pointer"
            @click="openPhotoPreview(photo)"
            >
            <img :src="photo.src" class="img-thumbnail" style="width: 100%;" />
            </div>
        </div>
    </b-modal>

    <b-modal v-model="previewModalVisible" title="Aperçu" hide-footer size="lg">
        <div v-if="selectedPhoto" class="text-center">
            <img :src="selectedPhoto.src" class="img-fluid" />
            <p class="mt-2"><strong>{{ selectedPhoto.nom }}</strong></p>
        </div>
    </b-modal>

     <!-- Modal interventions liées -->
     <b-modal v-model="showLinkedInterventionsModal" title="Interventions liées à cette tâche" hide-footer>
         <div v-if="linkedInterventions.length > 0">
           <ul class="list-group">
             <li v-for="intervention in linkedInterventions" :key="intervention.id" class="list-group-item d-flex justify-content-between align-items-center">
                               <div>
                  <strong>#{{ intervention.id }} - {{ intervention.title }}</strong><br>
                  <small class="text-muted">
                    <div>Client: {{ intervention.client_name || '— Aucun —' }}</div>
                    <div>Date: {{ new Date(intervention.date_intervention).toLocaleDateString('fr-FR') }}</div>
                  </small>
                </div>
                               <div class="d-flex gap-2">
                  <b-button size="sm" variant="primary" @click="showInterventionDetails(intervention)">
                    Voir
                  </b-button>
                  <b-button size="sm" variant="danger" @click="unlinkIntervention(intervention.id)">
                    Délier
                  </b-button>
                </div>
             </li>
           </ul>
         </div>
         <div v-else class="text-center text-muted">
           Aucune intervention liée à cette tâche.
         </div>
     </b-modal>

           <!-- Modal lier intervention existante -->
      <b-modal v-model="showLinkInterventionModal" title="Lier une intervention existante" hide-footer>
          <b-form-group label="Sélectionner une intervention :">
            <b-form-select v-model="selectedInterventionToLink" :options="availableInterventions" value-field="id" text-field="title">
              <template #first>
                <option :value="null">Choisir une intervention...</option>
              </template>
            </b-form-select>
          </b-form-group>
          <div class="text-end mt-3">
            <b-button variant="secondary" @click="showLinkInterventionModal = false">Annuler</b-button>
            <b-button variant="primary" @click="linkIntervention" :disabled="!selectedInterventionToLink">
              Lier
            </b-button>
          </div>
      </b-modal>

      <!-- Modal documents liés -->
      <b-modal v-model="showLinkedDocumentsModal" title="Documents liés à cette tâche" hide-footer>
          <div v-if="linkedDocuments.length > 0">
            <ul class="list-group">
              <li v-for="document in linkedDocuments" :key="document.id" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <strong>{{ document.nom }}</strong><br>
                  <small class="text-muted">
                    <div>Type: {{ document.type_mime }}</div>
                    <div>Taille: {{ formatFileSize(document.taille) }}</div>
                    <div>Créé le: {{ formatDateFR(document.date_creation) }}</div>
                  </small>
                </div>
                <div class="d-flex gap-2">
                  <b-button size="sm" variant="primary" @click="downloadDocument(document)">
                    Télécharger
                  </b-button>
                  <b-button size="sm" variant="danger" @click="unlinkDocument(document.id)">
                    Délier
                  </b-button>
                </div>
              </li>
            </ul>
          </div>
          <div v-else class="text-center text-muted">
            Aucun document lié à cette tâche.
          </div>
      </b-modal>

      <!-- Modal lier document existant -->
      <b-modal v-model="showLinkDocumentModal" title="Lier un document existant" hide-footer>
          <b-form-group label="Sélectionner un document :">
            <b-form-select v-model="selectedDocumentToLink" :options="availableDocuments" value-field="id" text-field="nom">
              <template #first>
                <option :value="null">Choisir un document...</option>
              </template>
            </b-form-select>
          </b-form-group>
          <div class="text-end mt-3">
            <b-button variant="secondary" @click="showLinkDocumentModal = false">Annuler</b-button>
            <b-button variant="primary" @click="linkDocument" :disabled="!selectedDocumentToLink">
              Lier
            </b-button>
          </div>
      </b-modal>

      <!-- Modal détail intervention -->
      <b-modal v-model="showInterventionDetailModal" title="Détail de l'intervention" hide-footer>
          <div v-if="selectedIntervention">
            <p><strong>ID :</strong> {{ selectedIntervention.id }}</p>
            <p><strong>Titre :</strong> {{ selectedIntervention.title }}</p>
            <p><strong>Client :</strong> {{ selectedIntervention.client_name || '— Aucun —' }}</p>
            <p><strong>Utilisateur :</strong> {{ selectedIntervention.user_name || '—' }}</p>
            <p><strong>Date d'intervention :</strong> {{ new Date(selectedIntervention.date_intervention).toLocaleDateString('fr-FR') }}</p>
            <p v-if="selectedIntervention.heure_arrivee && selectedIntervention.heure_depart">
              <strong>Heure d'arrivée :</strong> {{ selectedIntervention.heure_arrivee }}
            </p>
            <p v-if="selectedIntervention.heure_arrivee && selectedIntervention.heure_depart">
              <strong>Heure de départ :</strong> {{ selectedIntervention.heure_depart }}
            </p>
            <p v-if="!selectedIntervention.heure_arrivee || !selectedIntervention.heure_depart" class="text-muted">
              <i class="fas fa-info-circle me-2"></i>
              Cette intervention n'a pas de durée définie (pas d'heure d'arrivée ni de départ).
            </p>
            <p><strong>Responsable :</strong> {{ selectedIntervention.accountable || '—' }}</p>
            <div v-if="selectedIntervention.memo_utilisateur">
              <p><strong>Mémo interne :</strong></p>
              <div class="memo-display" v-html="cleanMemoHtml(selectedIntervention.memo_utilisateur)"></div>
            </div>
            <div v-else>
              <p><strong>Mémo interne :</strong></p>
              <p class="text-muted">— Aucun mémo —</p>
            </div>
            
            <div v-if="selectedIntervention.memo_client">
              <p><strong>Mémo client :</strong></p>
              <div class="memo-display" v-html="cleanMemoHtml(selectedIntervention.memo_client)"></div>
            </div>
            <div v-else>
              <p><strong>Mémo client :</strong></p>
              <p class="text-muted">— Aucun mémo —</p>
            </div>
          </div>
      </b-modal>

      <!-- Modal mémo complet -->
      <b-modal v-model="showMemoModal" title="Mémo complet" hide-footer>
        <div v-if="selectedMemo" class="task-memo-full-content">
          <div v-html="cleanMemoHtml(selectedMemo)" class="task-memo-text-full"></div>
        </div>
        <div v-else class="text-muted">— Aucun mémo —</div>
      </b-modal>

  </template>
  
  <script>
  import api from '@/plugins/api';
  import FooterActions from '@/components/FooterActions.vue';
  
  /**
   * Page de gestion des tâches
   * Permet de :
   * - Lister toutes les tâches avec filtres
   * - Créer de nouvelles tâches
   * - Consulter les détails d'une tâche
   * - Voir les photos associées
   * - Changer le statut des tâches
   */
  export default {
    name: 'TasksPage',
    components: { FooterActions },
    data() {
      return {
        // Données principales
        tasks: [], // Liste de toutes les tâches
        clientOptions: [], // Options pour le filtre client
        currentUser: null, // Utilisateur connecté
        
        // Filtres actifs
        selectedClient: null, // Client sélectionné pour le filtre
        selectedStatut: null, // Statut sélectionné pour le filtre
        search: '', // Terme de recherche par titre
        showAllTasks: false, // Afficher toutes les tâches ou seulement celles de l'utilisateur
        // Variables pour le filtre client
        clientFilterSearch: '',
        filteredClientFilterOptions: [],
        showClientFilterResults: false,
        
        // Modal de création
        showNewTaskModal: false, // Affichage du modal de création
        newTaskTitle: '', // Titre de la nouvelle tâche
        newTaskClient: null, // Client de la nouvelle tâche
        
        // Modal de détail
        isDetailModalVisible: false, // Affichage du modal de détail
        detailTask: null, // Tâche sélectionnée pour le détail
        // Options de statut pour les filtres et sélection
        statutOptions: [
          { value: 'en_cours', text: 'En cours' },
          { value: 'terminee', text: 'Terminée' },
          { value: 'annulee', text: 'Annulée' }
        ],
        
        // Gestion des photos
        photoModalVisible: false, // Affichage du modal des photos
        previewModalVisible: false, // Affichage du modal d'aperçu photo
        photoModalPhotos: [], // Photos de la tâche sélectionnée
        selectedPhoto: null, // Photo sélectionnée pour aperçu

                   // Gestion des interventions liées
          linkedInterventions: [], // Interventions liées à la tâche
          showLinkedInterventionsModal: false, // Modal pour voir les interventions liées
          showLinkInterventionModal: false, // Modal pour lier une intervention existante
          availableInterventions: [], // Liste des interventions disponibles pour liaison
          selectedInterventionToLink: null, // Intervention sélectionnée pour liaison
          
          // Modal détail intervention
          showInterventionDetailModal: false, // Modal pour voir les détails d'une intervention
          selectedIntervention: null, // Intervention sélectionnée pour affichage des détails
          
          // Modal mémo complet
          showMemoModal: false, // Modal pour afficher le mémo complet
          selectedMemo: '', // Mémo sélectionné pour affichage complet

          // Gestion des documents liés
          linkedDocuments: [], // Documents liés à la tâche
          showLinkedDocumentsModal: false, // Modal pour voir les documents liés
          showLinkDocumentModal: false, // Modal pour lier un document existant
          availableDocuments: [], // Liste des documents disponibles pour liaison
          selectedDocumentToLink: null, // Document sélectionné pour liaison

      };
    },
    computed: {
      /**
       * Filtre les tâches selon les critères sélectionnés
       * Applique les filtres : client, statut et recherche
       * Par défaut, affiche les tâches attribuées à l'utilisateur connecté ou non attribuées
       * Si showAllTasks est true, affiche toutes les tâches
       * @returns {Array} Liste des tâches filtrées
       */
      filteredTasks() {
        return this.tasks.filter(t => {
          // Filtre par défaut : tâches attribuées à l'utilisateur connecté ou non attribuées
          const isAssignedToCurrentUser = this.currentUser && t.assigned_user_id === this.currentUser.id;
          const isUnassigned = !t.assigned_user_id;
          const passesDefaultFilter = this.showAllTasks || isAssignedToCurrentUser || isUnassigned;
          
          return (
            passesDefaultFilter &&
            (!this.selectedClient || t.client === this.selectedClient) &&
            (!this.selectedStatut || t.statut === this.selectedStatut) &&
            (!this.search || t.title?.toLowerCase().includes(this.search.toLowerCase()))
          );
        });
      }
    },
    /**
     * Initialisation du composant
     * Charge toutes les données nécessaires au démarrage
     */
    mounted() {
      this.loadCurrentUser(); // Charge l'utilisateur connecté
      this.fetchTasks(); // Charge la liste des tâches
      this.fetchClients(); // Charge la liste des clients
    },
    methods: {
      // Fonction pour nettoyer le HTML du mémo
      cleanMemoHtml(html) {
        if (!html) return '';
        return html
          .replace(/<p>/g, '')
          .replace(/<\/p>/g, '<br>')
          .replace(/<br><br>/g, '<br>') // Éviter les doubles sauts de ligne
          .replace(/<br>$/g, ''); // Supprimer le dernier <br>
      },

      /**
       * Charge l'utilisateur connecté depuis le localStorage
       */
      loadCurrentUser() {
        try {
          const raw = localStorage.getItem('user');
          if (raw) {
            this.currentUser = JSON.parse(raw);
          }
        } catch (e) {
          console.error('Erreur chargement utilisateur', e);
        }
      },

      /**
       * Bascule entre l'affichage de toutes les tâches et l'affichage filtré
       */
      toggleShowAllTasks() {
        this.showAllTasks = !this.showAllTasks;
      },

      /**
       * Charge la liste complète des tâches depuis l'API
       */
      async fetchTasks() {
        const { data } = await api.get('/tasks');
        this.tasks = data;
      },

      /**
       * Charge la liste des contacts pour les filtres et la création
       */
      async fetchClients() {
        const { data } = await api.get('/tasks/contacts');
        this.clientOptions = data.map(c => ({ value: c.codechrono, text: c.nom_affichage }));
        // Initialiser aussi la liste pour le filtre
        this.filteredClientFilterOptions = this.clientOptions.slice(0, 20);
      },

      // Méthodes pour le filtre client
      filterClientsForFilter() {
        if (!this.clientFilterSearch.trim()) {
          // Afficher tous les clients par défaut (limités à 20 pour les performances)
          this.filteredClientFilterOptions = this.clientOptions.slice(0, 20);
          return;
        }
        
        const searchTerm = this.clientFilterSearch.toLowerCase();
        this.filteredClientFilterOptions = this.clientOptions.filter(client => 
          client.text.toLowerCase().includes(searchTerm)
        ).slice(0, 10); // Limiter à 10 résultats pour la recherche
      },

      selectClientForFilter(client) {
        this.selectedClient = client.value;
        this.clientFilterSearch = client.text;
        this.filteredClientFilterOptions = [];
      },

      getSelectedClientFilterName() {
        const client = this.clientOptions.find(c => c.value === this.selectedClient);
        return client ? client.text : '';
      },

      // Méthodes pour le focus/blur du filtre
      onClientFilterFocus() {
        // Vider le champ et réinitialiser la sélection quand on clique dessus
        this.clientFilterSearch = '';
        this.selectedClient = null;
        this.showClientFilterResults = true;
        this.filteredClientFilterOptions = this.clientOptions.slice(0, 20);
      },

      onClientFilterBlur() {
        // Délai pour permettre le clic sur un élément de la liste
        setTimeout(() => {
          this.showClientFilterResults = false;
        }, 200);
      },



      /**
       * Crée une nouvelle tâche
       * Redirige vers la page d'édition après création
       */
      async createTask() {
        try {
          const payload = {
            title: this.newTaskTitle,
            client: this.newTaskClient,
            statut: 'en_cours', // Ajouté le statut par défaut
          };
          const { data } = await api.post('/tasks', payload);
          this.showNewTaskModal = false;
          this.newTaskTitle = '';
          this.newTaskClient = null;
          this.$router.push({ name: 'task-edit', params: { id: data.id } });
        } catch (err) {
          console.error("Erreur lors de la création de la tâche:", err);
          alert("Erreur lors de la création de la tâche. Veuillez réessayer.");
        }
      },

      /**
       * Ouvre le modal de détail d'une tâche
       * Charge les photos associées à la tâche
       */
      async openDetailModal(task) {
        this.detailTask = task;
        try {
            const res = await api.get(`/tasks/${task.id}/photos`);
            this.photoModalPhotos = res.data.map(p => ({
            id: p.id,
            nom: p.nom,
            src: `data:image/png;base64,${p.image}`
            }));
        } catch (err) {
            console.error("Erreur chargement photos :", err);
            this.photoModalPhotos = [];
        }

         // Charger les interventions liées
         await this.loadLinkedInterventions();
         // Charger les documents liés
         await this.loadLinkedDocuments();
         
        this.isDetailModalVisible = true;
        },

      /**
       * Annule une tâche (change le statut à 'annulee')
       * Demande confirmation avant modification
       */
      async cancelTask(id) {
        if (!confirm('Annuler cette tâche ?')) return;
        try {
          await api.patch(`/tasks/${id}`, { statut: 'annulee' });
          await this.fetchTasks();
          this.isDetailModalVisible = false;
        } catch (e) {
            console.error('Erreur lors de l\'annulation de la tâche', e);
            alert('Erreur lors de l\'annulation de la tâche');
        }
      },

      /**
       * Redirige vers la page d'édition d'une tâche
       * Ferme le modal de détail avant redirection
       */
      goToEdit(id) {
        this.isDetailModalVisible = false;
        this.$router.push({ name: 'task-edit', params: { id } });
      },

      /**
       * Retourne les informations d'affichage pour un statut
       * @param {string} statut - Le statut de la tâche
       * @returns {Object} {label: string, color: string}
       */
      getStatutLabel(statut) {
        switch (statut) {
          case 'en_cours': return { label: 'En cours', color: 'warning' };
          case 'terminee': return { label: 'Terminée', color: 'success' };
          case 'annulee': return { label: 'Annulée', color: 'danger' };
          default: return { label: 'Inconnu', color: 'dark' };
        }
      },

      /**
       * Ouvre l'aperçu d'une photo en plein écran
       */
      openPhotoPreview(photo) {
        this.selectedPhoto = photo;
        this.previewModalVisible = true;
              },

      /**
       * Ferme l'aperçu d'une photo
       */
      closePhotoPreview() {
        this.previewModalVisible = false;
        this.selectedPhoto = null;
        },

      /**
       * Change le statut d'une tâche
       * Met à jour l'affichage après modification
       */
      async changeTaskStatus(taskId, newStatut) {
        try {
            await api.patch(`/tasks/${taskId}`, { statut: newStatut });
            await this.fetchTasks();
            this.isDetailModalVisible = false;
        } catch (err) {
            console.error("Erreur lors du changement de statut :", err);
            alert("Erreur lors du changement de statut. Veuillez réessayer.");
        }
      },

      /**
       * Crée une intervention à partir d'une tâche
       * Reprend les informations de la tâche pour pré-remplir l'intervention
       * Utilise la même logique de calcul des heures que la modal de création d'intervention
       */
      async createInterventionFromTask() {
        try {
          if (!this.detailTask) {
            console.error('Aucune tâche sélectionnée');
            return;
          }

          // Demander à l'utilisateur s'il veut une intervention avec ou sans durée
          const hasDuration = confirm('Voulez-vous créer une intervention avec une durée (heure d\'arrivée et de départ) ?\n\nCliquez sur "OK" pour une intervention avec durée\nCliquez sur "Annuler" pour une intervention sans durée');

          // Préparer les données de base de l'intervention
          const interventionData = {
            title: this.detailTask.title,
            client: this.detailTask.client || null,
            memo_utilisateur: this.detailTask.memo || '',
            // Utiliser l'utilisateur connecté comme utilisateur de l'intervention
            utilisateur: this.currentUser.id,
            // Date d'intervention (aujourd'hui par défaut)
            date_intervention: new Date().toISOString().substring(0,10),
            accountable: '',
            memo_client: '',
            // Photos de la tâche (si disponibles)
            client_photos_ids: [],
            utilisateurs_photos_ids: []
          };

          // Ajouter les heures seulement si l'intervention a une durée
          if (hasDuration) {
            // Calcul des heures (même logique que la modal de création d'intervention)
            const now = new Date();
            let depHour = now.getHours();
            let depMinute = now.getMinutes();
            if (depMinute <= 7) {
              depMinute = 0;
            } else if (depMinute <= 22) {
              depMinute = 15;
            } else if (depMinute <= 37) {
              depMinute = 30;
            } else if (depMinute <= 52) {
              depMinute = 45;
            } else {
              depMinute = 0;
              depHour = (depHour + 1) % 24;
            }
            let arrHour = depHour;
            let arrMinute = depMinute - 15;
            if (arrMinute < 0) {
              arrMinute = 45;
              arrHour = (arrHour - 1 + 24) % 24;
            }
            const pad = n => String(n).padStart(2, '0');

            // Calcul de la date en fonction de l'heure d'arrivée
            let interventionDate = new Date();
            if (arrHour > depHour) {
              // Si l'heure d'arrivée est plus grande que l'heure de départ, 
              // c'est que l'arrivée est le jour précédent
              interventionDate.setDate(interventionDate.getDate() - 1);
            }

            interventionData.date_intervention = interventionDate.toISOString().substring(0,10);
            interventionData.heure_arrivee = `${pad(arrHour)}:${pad(arrMinute)}:00`;
            interventionData.heure_depart = `${pad(depHour)}:${pad(depMinute)}:00`;
          } else {
            // Pour les interventions sans durée, on met des valeurs null
            interventionData.heure_arrivee = null;
            interventionData.heure_depart = null;
          }

          console.log('Création d\'intervention à partir de la tâche:', {
            taskId: this.detailTask.id,
            taskTitle: this.detailTask.title,
            interventionData
          });

          // Créer l'intervention
          const { data } = await api.post('/interventions/', interventionData);
          
          console.log('Intervention créée avec succès:', data);

          // Lier automatiquement l'intervention à la tâche
          try {
            await api.post(`/tasks/${this.detailTask.id}/interventions/${data.id}/link`);
            console.log('Intervention liée à la tâche avec succès');
          } catch (linkError) {
            console.error('Erreur lors de la liaison à la tâche:', linkError);
            // On continue même si la liaison échoue
          }

          // Fermer le modal de détail
          this.isDetailModalVisible = false;

          // Rediriger vers la page d'édition de l'intervention
          this.$router.push({ 
            name: 'intervention-edit', 
            params: { id: data.id } 
          });

                 } catch (error) {
           console.error('Erreur lors de la création de l\'intervention:', error);
           alert('Erreur lors de la création de l\'intervention. Veuillez réessayer.');
         }
       },

       /**
        * Charge les interventions liées à la tâche actuelle
        */
       async loadLinkedInterventions() {
         if (!this.detailTask) return;
         
         console.log('Chargement des interventions liées pour la tâche:', this.detailTask.id);
         
         try {
           const { data } = await api.get(`/tasks/${this.detailTask.id}/interventions`);
           console.log('Interventions liées reçues:', data);
           this.linkedInterventions = data;
         } catch (error) {
           console.error('Erreur lors du chargement des interventions liées:', error);
           console.error('Détails de l\'erreur:', error.response?.data);
           this.linkedInterventions = [];
         }
       },

               /**
         * Charge les interventions disponibles pour liaison
         */
        async loadAvailableInterventions() {
          try {
            const { data } = await api.get('/interventions/', { params: { include_photos: false } });
            // Filtrer les interventions déjà liées
            const linkedIds = this.linkedInterventions.map(i => i.id);
            // Exclure les interventions traitées (2) et annulées (3)
            this.availableInterventions = data
              .filter(intervention => !linkedIds.includes(intervention.id))
              .filter(intervention => intervention.statut !== 2 && intervention.statut !== 3);
          } catch (error) {
            console.error('Erreur lors du chargement des interventions disponibles:', error);
            this.availableInterventions = [];
          }
        },

        /**
         * Charge les interventions disponibles quand on ouvre le modal de liaison
         */
        async openLinkInterventionModal() {
          this.showLinkInterventionModal = true;
          await this.loadAvailableInterventions();
        },

        /**
         * Ouvre le modal des interventions liées et charge les données
         */
        async openLinkedInterventionsModal() {
          console.log('Ouverture du modal des interventions liées');
          await this.loadLinkedInterventions();
          console.log('Modal ouvert, interventions:', this.linkedInterventions);
          this.showLinkedInterventionsModal = true;
        },

       /**
        * Lie une intervention existante à la tâche
        */
       async linkIntervention() {
         if (!this.selectedInterventionToLink || !this.detailTask) return;
         
         try {
           await api.post(`/tasks/${this.detailTask.id}/interventions/${this.selectedInterventionToLink}/link`);
           this.showLinkInterventionModal = false;
           this.selectedInterventionToLink = null;
           await this.loadLinkedInterventions();
           await this.loadAvailableInterventions();
         } catch (error) {
           console.error('Erreur lors de la liaison de l\'intervention:', error);
           alert('Erreur lors de la liaison de l\'intervention. Veuillez réessayer.');
         }
       },

       /**
        * Délie une intervention de la tâche
        */
       async unlinkIntervention(interventionId) {
         if (!this.detailTask) return;
         
         if (!confirm('Êtes-vous sûr de vouloir délier cette intervention ?')) return;
         
         try {
           await api.delete(`/tasks/${this.detailTask.id}/interventions/${interventionId}/unlink`);
           await this.loadLinkedInterventions();
           await this.loadAvailableInterventions();
         } catch (error) {
           console.error('Erreur lors de la déliaison de l\'intervention:', error);
           alert('Erreur lors de la déliaison de l\'intervention. Veuillez réessayer.');
         }
       },

               /**
         * Affiche les détails d'une intervention dans une modal
         */
        showInterventionDetails(intervention) {
          this.selectedIntervention = intervention;
          this.showInterventionDetailModal = true;
        },

        /**
         * Ouvre la modal pour afficher le mémo complet
         */
        openMemoModal(memo) {
          this.selectedMemo = memo;
          this.showMemoModal = true;
        },

        // Méthodes pour les documents liés
        async loadLinkedDocuments() {
          if (!this.detailTask) return;
          
          try {
            const { data } = await api.get(`/tasks/${this.detailTask.id}/documents`);
            this.linkedDocuments = data;
          } catch (error) {
            console.error('Erreur lors du chargement des documents liés:', error);
            this.linkedDocuments = [];
          }
        },

        async loadAvailableDocuments() {
          try {
            const { data } = await api.get('/document-files');
            console.log('Tous les documents récupérés:', data.length);
            
            // Filtrer seulement les documents déjà liés à cette tâche spécifique
            const linkedIds = this.linkedDocuments.map(d => d.id);
            console.log('Documents déjà liés à cette tâche:', linkedIds);
            
            this.availableDocuments = data.filter(document => !linkedIds.includes(document.id));
            console.log('Documents disponibles après filtrage:', this.availableDocuments.length);
          } catch (error) {
            console.error('Erreur lors du chargement des documents disponibles:', error);
            this.availableDocuments = [];
          }
        },

        async openLinkDocumentModal() {
          this.showLinkDocumentModal = true;
          // Charger d'abord les documents liés à cette tâche, puis les documents disponibles
          await this.loadLinkedDocuments();
          await this.loadAvailableDocuments();
        },

        async linkDocument() {
          if (!this.selectedDocumentToLink || !this.detailTask) return;
          
          try {
            await api.post(`/tasks/${this.detailTask.id}/documents/${this.selectedDocumentToLink}/link`);
            this.showLinkDocumentModal = false;
            this.selectedDocumentToLink = null;
            await this.loadLinkedDocuments();
            await this.loadAvailableDocuments();
          } catch (error) {
            console.error('Erreur lors de la liaison du document:', error);
            alert('Erreur lors de la liaison du document. Veuillez réessayer.');
          }
        },

        async unlinkDocument(documentId) {
          if (!this.detailTask) return;
          
          if (!confirm('Êtes-vous sûr de vouloir délier ce document ?')) return;
          
          try {
            await api.delete(`/tasks/${this.detailTask.id}/documents/${documentId}/unlink`);
            await this.loadLinkedDocuments();
            await this.loadAvailableDocuments();
          } catch (error) {
            console.error('Erreur lors de la déliaison du document:', error);
            alert('Erreur lors de la déliaison du document. Veuillez réessayer.');
          }
        },

        async downloadDocument(doc) {
          try {
            // Télécharger le document via l'API
            const response = await api.get(`/document-files/${doc.id}/download`, {
              responseType: 'blob'
            });
            
            // Créer un blob et télécharger
            const blob = new Blob([response.data], { 
              type: doc.type_mime || 'application/octet-stream' 
            });
            
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.nom || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Nettoyer l'URL
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error('Erreur lors du téléchargement du document:', error);
            alert('Erreur lors du téléchargement du document. Veuillez réessayer.');
          }
        },

        formatFileSize(bytes) {
          if (!bytes) return '0 B';
          const k = 1024;
          const sizes = ['B', 'KB', 'MB', 'GB'];
          const i = Math.floor(Math.log(bytes) / Math.log(k));
          return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        // Formate une date au format français (DD-MM-YYYY)
        formatDateFR(dateStr) {
          if (!dateStr) return '';
          // Prend en compte le fuseau local (français) avec Date()
          const d = new Date(dateStr);
          // On récupère le jour, mois, année en local
          const day   = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0'); // 0-indexé
          const year  = d.getFullYear();
          return `${day}-${month}-${year}`;
        }
    }
  };
  </script>
  
  <style scoped>
  .tasks-page {
    max-width: 800px;
    margin: auto;
    padding-bottom: 100px;
  }
  .list-group-item {
    cursor: pointer;
  }
  .filters .form-group {
    min-width: 160px;
  }
  
  /* Styles pour le contenu du mémo */
  .task-memo-content {
    margin-top: 0.5rem;
    max-height: 200px;
    overflow-y: auto;
  }
  
  .task-memo-content p {
    margin-bottom: 0.5rem;
  }
  
  .task-memo-content p:last-child {
    margin-bottom: 0;
   }
   
   /* Styles pour le contenu du mémo des interventions */
   .intervention-memo-content {
     margin-top: 0.5rem;
     max-height: 200px;
     overflow-y: auto;
   }
   
   .intervention-memo-content p {
     margin-bottom: 0.5rem;
   }
   
   .intervention-memo-content p:last-child {
     margin-bottom: 0;
   }

   /* Styles pour l'affichage tronqué du mémo des tâches */
   .task-memo-display-truncated {
     background-color: #f8f9fa;
     border: 1px solid #dee2e6;
     border-radius: 8px;
     padding: 1rem;
     margin: 0.5rem 0;
     cursor: pointer;
     position: relative;
     transition: all 0.2s ease;
     height: 120px; /* Hauteur pour 3 lignes environ */
     overflow: hidden;
   }
   
   .task-memo-display-truncated:hover {
     border-color: #2768b1;
     box-shadow: 0 4px 8px rgba(39, 104, 177, 0.2);
   }
   
   .task-memo-content-truncated {
     line-height: 1.6;
     color: #495057;
     font-family: inherit;
     font-size: 1rem;
     margin: 0;
     padding: 0;
     height: 100%;
     overflow: hidden;
   }
   
   .task-memo-overlay-truncated {
     position: absolute;
     bottom: 0;
     left: 0;
     right: 0;
     background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
     padding: 1rem;
     text-align: center;
     pointer-events: none;
   }
   
   .task-click-hint-truncated {
     background-color: #2768b1;
     color: white;
     padding: 0.25rem 0.75rem;
     border-radius: 20px;
     font-size: 0.8rem;
     font-weight: 500;
   }

   /* Styles pour la recherche de clients */
   .client-search-results {
     max-height: 200px;
     max-width: 300px;
     overflow-y: auto;
     border: 1px solid #dee2e6;
     border-radius: 4px;
     background-color: white;
     box-shadow: 0 2px 4px rgba(0,0,0,0.1);
     position: absolute;
     top: 100%;
     left: 0;
     right: 0;
     z-index: 1000;
     margin-top: 2px;
   }

   .client-option {
     transition: background-color 0.2s ease;
   }

   .client-option:hover {
     background-color: #f8f9fa !important;
   }

   .client-option.cursor-pointer {
     cursor: pointer;
   }

   /* Style pour l'affichage du client sélectionné */
   .selected-client-display {
     background-color: #d4edda;
     border: 1px solid #c3e6cb;
     border-radius: 6px;
     padding: 12px 16px;
     color: #155724;
     font-size: 1.1rem;
     display: flex;
     align-items: center;
     justify-content: center;
     box-shadow: 0 2px 4px rgba(0,0,0,0.1);
     max-width: 250px;
     margin: 0 auto;
   }

   .selected-client-display i {
     color: #28a745;
     font-size: 1.2rem;
   }

   /* Conteneur pour le positionnement absolu */
   .client-filter-container {
     position: relative;
   }
   
   /* Styles pour la modal du mémo complet */
   .task-memo-full-content {
     max-height: 60vh;
     overflow-y: auto;
   }
   
      .task-memo-text-full {
     white-space: pre-wrap;
     word-wrap: break-word;
     line-height: 1.6;
     color: #495057;
     font-family: inherit;
     font-size: 1rem;
     margin: 0;
     padding: 1rem;
     background-color: #f8f9fa;
     border-radius: 8px;
     border: 1px solid #dee2e6;
   }

   /* Styles pour l'affichage des mémo */
   .memo-display {
     background-color: #f8f9fa;
     border: 1px solid #dee2e6;
     border-radius: 4px;
     padding: 0.75rem;
     margin: 0.5rem 0;
     white-space: pre-wrap;
     word-wrap: break-word;
     line-height: 1.5;
     color: #495057;
     font-family: inherit;
     font-size: 0.9rem;
   }

   /* Padding pour éviter que le contenu soit masqué par le footer */
   .modal-detail-content {
     padding-bottom: 80px !important;
   }

  /* Alignement type interventions */
  .intervention-info-group {
    margin: 1rem 0;
  }
  .info-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.1rem;
  }
  .info-label {
    min-width: 80px;
    text-align: left;
  }
  .info-separator {
    margin: 0 0.5rem;
    text-align: center;
    min-width: 10px;
  }
  .info-value {
    flex: 1;
    text-align: left;
  }

  /* Mémo avec titre sur la bordure (même style que interventions) */
  .memo-container {
    position: relative;
    margin: 1rem 0;
  }
  .memo-title-overlay {
    position: absolute;
    top: -12px;
    left: 15px;
    background: white;
    padding: 0 8px;
    font-weight: bold;
    font-size: 0.9rem;
    color: #000000;
    z-index: 10;
  }
  .memo-display-truncated {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.5rem 0;
    cursor: pointer;
    position: relative;
    transition: all 0.2s ease;
    height: 120px;
    overflow: hidden;
  }
  .memo-display-truncated:hover {
    border-color: #2768b1;
    box-shadow: 0 4px 8px rgba(39, 104, 177, 0.2);
  }
  .memo-content-truncated {
    line-height: 1.6;
    color: #495057;
    font-family: inherit;
    font-size: 1rem;
    margin: 0;
    padding: 0;
    height: 100%;
    overflow: hidden;
  }
  .memo-overlay-truncated {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(248, 249, 250, 0.9));
    padding: 1rem;
    text-align: center;
    pointer-events: none;
  }
  .click-hint-truncated {
    background-color: #2768b1;
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 500;
  }
  </style>
  